import { db } from '../App';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, addDoc, getDoc, serverTimestamp } from 'firebase/firestore';

let lastRequestTimestamp = null;
const debounceDelay = 1000; // 1 second debounce delay

export const searchUsersByName = async (name) => {
  try {
    if (name.trim().length < 2) {
      return [];
    }

    const lowercaseName = name.toLowerCase().trim();
    const usersRef = collection(db, 'users');
    const q = query(usersRef);
    const querySnapshot = await getDocs(q);
    const users = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();

      if (userData.name.toLowerCase().startsWith(lowercaseName)) {
        users.push({ id: doc.id, ...userData });
      }
    });
    return users;
  } catch (error) {
    console.error('Error searching users by name:', error);
    return [];
  }
};

export const sendFriendRequest = async (currentUserId, currentUserName, friendId, friendName) => {
  try {
    const now = Date.now();
    if (lastRequestTimestamp && now - lastRequestTimestamp < debounceDelay) {
      console.warn('Friend request ignored due to rapid consecutive requests');
      return;
    }
    lastRequestTimestamp = now;

    console.log(`Sending friend request from ${currentUserId} to ${friendId}`);

    const friendRequestRef = collection(db, 'users', friendId, 'friendRequests');
    const outgoingRequestRef = collection(db, 'users', currentUserId, 'outgoingRequests');

    await addDoc(friendRequestRef, {
      from: currentUserId,
      fromName: currentUserName,
      status: 'pending',
      timestamp: serverTimestamp()
    });

    await addDoc(outgoingRequestRef, {
      to: friendId,
      toName: friendName,
      status: 'pending',
      timestamp: serverTimestamp()
    });

    console.log(`Friend request sent from ${currentUserId} to ${friendId}`);
  } catch (error) {
    console.error('Error sending friend request:', error);
  }
};

export const acceptFriendRequest = async (currentUserId, currentUserName, requestId) => {
  try {
    console.log(`Accepting friend request ${requestId} for user ${currentUserId}`);
    const friendRequestRef = doc(db, 'users', currentUserId, 'friendRequests', requestId);
    await updateDoc(friendRequestRef, { status: 'accepted' });
    const requestSnap = await getDoc(friendRequestRef);
    const { from: friendId, fromName: friendName } = requestSnap.data();
    
    const friendDoc = await getDoc(doc(db, 'users', friendId));
    const friendProfilePicture = friendDoc.exists() ? friendDoc.data().profilePicture : null;

    await addDoc(collection(db, 'users', currentUserId, 'friends'), { friendId, friendName, profilePicture: friendProfilePicture });
    
    const currentUserDoc = await getDoc(doc(db, 'users', currentUserId));
    const currentUserProfilePicture = currentUserDoc.exists() ? currentUserDoc.data().profilePicture : null;
    
    await addDoc(collection(db, 'users', friendId, 'friends'), { friendId: currentUserId, friendName: currentUserName, profilePicture: currentUserProfilePicture });
    await deleteDoc(friendRequestRef);

    const outgoingRequestRef = query(collection(db, 'users', friendId, 'outgoingRequests'), where('to', '==', currentUserId));
    const outgoingRequestSnapshot = await getDocs(outgoingRequestRef);
    await Promise.all(outgoingRequestSnapshot.docs.map(doc => deleteDoc(doc.ref)));
  } catch (error) {
    console.error('Error accepting friend request:', error);
  }
};

export const denyFriendRequest = async (currentUserId, requestId) => {
  try {
    console.log(`Denying friend request ${requestId} for user ${currentUserId}`);
    const friendRequestRef = doc(db, 'users', currentUserId, 'friendRequests', requestId);
    const requestSnap = await getDoc(friendRequestRef);
    const { from: friendId } = requestSnap.data();
    
    await deleteDoc(friendRequestRef);

    const outgoingRequestRef = query(collection(db, 'users', friendId, 'outgoingRequests'), where('to', '==', currentUserId));
    const outgoingRequestSnapshot = await getDocs(outgoingRequestRef);
    await Promise.all(outgoingRequestSnapshot.docs.map(doc => deleteDoc(doc.ref)));
  } catch (error) {
    console.error('Error denying friend request:', error);
  }
};

export const getFriendRequests = async (userId) => {
  try {
    console.log(`Getting friend requests for user ${userId}`);
    const q = query(collection(db, 'users', userId, 'friendRequests'), where('status', '==', 'pending'));
    const querySnapshot = await getDocs(q);
    const friendRequests = [];
    for (const docSnap of querySnapshot.docs) {
      const request = { id: docSnap.id, ...docSnap.data() };
      const userDoc = await getDoc(doc(db, 'users', request.from));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        request.fromProfilePicture = userData.profilePicture;
        request.fromMembershipNumber = userData.membershipNumber;
      }
      friendRequests.push(request);
    }
    return friendRequests;
  } catch (error) {
    console.error('Error getting friend requests:', error);
    return [];
  }
};

export const getOutgoingRequests = async (userId) => {
  try {
    console.log(`Getting outgoing friend requests for user ${userId}`);
    const q = query(collection(db, 'users', userId, 'outgoingRequests'), where('status', '==', 'pending'));
    const querySnapshot = await getDocs(q);
    const outgoingRequests = [];
    for (const docSnap of querySnapshot.docs) {
      const outgoingRequest = { id: docSnap.id, ...docSnap.data() };
      const userDoc = await getDoc(doc(db, 'users', outgoingRequest.to));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        outgoingRequest.profilePicture = userData.profilePicture;
        outgoingRequest.toMembershipNumber = userData.membershipNumber;
      }
      outgoingRequests.push(outgoingRequest);
    }
    return outgoingRequests;
  } catch (error) {
    console.error('Error getting outgoing friend requests:', error);
    return [];
  }
};

export const getFriends = async (userId) => {
  try {
    console.log(`Getting friends for user ${userId}`);
    const q = query(collection(db, 'users', userId, 'friends'));
    const querySnapshot = await getDocs(q);
    const friends = [];
    for (const docSnap of querySnapshot.docs) {
      const friend = { id: docSnap.id, ...docSnap.data() };
      const userDoc = await getDoc(doc(db, 'users', friend.friendId));
      if (userDoc.exists()) {
        const { profilePicture, status, money, membershipNumber } = userDoc.data();
        friend.profilePicture = profilePicture;
        friend.status = status;
        friend.money = money;
        friend.membershipNumber = membershipNumber;
      }
      friends.push(friend);
    }
    return friends;
  } catch (error) {
    console.error('Error getting friends:', error);
    return [];
  }
};

export const removeFriend = async (currentUserId, friendId) => {
  try {
    console.log(`Removing friend relationship between ${currentUserId} and ${friendId}`);
    const currentUserFriendRef = query(collection(db, 'users', currentUserId, 'friends'), where('friendId', '==', friendId));
    const friendUserFriendRef = query(collection(db, 'users', friendId, 'friends'), where('friendId', '==', currentUserId));
    
    const currentUserFriendSnapshot = await getDocs(currentUserFriendRef);
    const friendUserFriendSnapshot = await getDocs(friendUserFriendRef);

    await Promise.all(currentUserFriendSnapshot.docs.map(doc => deleteDoc(doc.ref)));
    await Promise.all(friendUserFriendSnapshot.docs.map(doc => deleteDoc(doc.ref)));

    console.log(`Removed friend relationship between ${currentUserId} and ${friendId}`);
  } catch (error) {
    console.error('Error removing friend:', error);
  }
};

export const revokeOutgoingRequest = async (currentUserId, requestId) => {
  try {
    console.log(`Revoking outgoing friend request ${requestId} for user ${currentUserId}`);
    const outgoingRequestRef = doc(db, 'users', currentUserId, 'outgoingRequests', requestId);
    const requestSnap = await getDoc(outgoingRequestRef);
    if (!requestSnap.exists()) {
      throw new Error('Outgoing request does not exist');
    }
    const { to: friendId } = requestSnap.data() || {};
    
    await deleteDoc(outgoingRequestRef);

    const friendRequestRef = query(collection(db, 'users', friendId, 'friendRequests'), where('from', '==', currentUserId));
    const friendRequestSnapshot = await getDocs(friendRequestRef);
    await Promise.all(friendRequestSnapshot.docs.map(doc => deleteDoc(doc.ref)));
  } catch (error) {
    console.error('Error revoking outgoing friend request:', error);
  }
};
